// ============================================================================
// RealPayrollTable (L05) — tableau « coût réel courant » (376 L PoC)
// ============================================================================
// Port FIDELE du PoC components/salary/real-payroll-table.tsx (376 L @ SHA
// 7e40e4a). PUREMENT presentationnel : les montants prorata (lib/salary/payroll)
// sont calcules SERVEUR et serialises (RealPayrollRow). Section « Réel » de la
// planification, filtree a l'equipe du directeur.
//
// TYPE : `RealPayrollRow` + `realPayrollRowCost` viennent de ../../lib/salary/types
// (deja portes — le hook SL-A les importe de la). NE PAS redefinir (addendum SL-C).
//
// M-661 / ADR-071 §6 point 5 c et dette D2 : cet ecran est une PROJECTION/budget — le libelle est
// « coût réel COURANT », jamais « réellement payé ». Les taux individuels
// (gridRate/realRate) sont la surface PoC EXACTE, legitime (§G director
// canReadCompensation) — PAS une violation M-657 (qui couvre la frise agregee).
//
// TRANSPOSITION (§SL-C) : Alert @/components/ui → banniere locale warning ;
// Badge → Badge(tone)+title de ../employee/ui ; formats fr-CA figes → helpers
// locale (formatCad/formatHourly/formatSignedHourly/formatSignedPct/formatPctPlain,
// P14) ; tokens shadcn → slate ; libelles FR en dur → i18n FR+EN (P13). Regle #4
// zero emoji ; Regle #28 tokens dark-aware, zero CSS en dur cote couleur.

import { AlertTriangle } from 'lucide-react';

import { useTranslation } from '../../i18n';
import { Badge } from '../employee/ui';
import {
  formatCad,
  formatDecimal,
  formatHourly,
  formatSignedHourly,
  formatSignedPct,
  formatPctPlain,
} from './format';
import { type RealPayrollRow, realPayrollRowCost } from '../../lib/salary/types';
import type { OvertimeRule } from '../../lib/salary/special-hours';

export type { RealPayrollRow };

export function RealPayrollTable({
  rows,
  year,
  showTeam = true,
  countRow = (r) => r.active,
  periodLabel,
  overtimeRule = null,
}: {
  rows: RealPayrollRow[];
  year: number;
  showTeam?: boolean;
  // Predicat des lignes comptees dans le total. Par defaut : employes ACTIFS.
  countRow?: (r: RealPayrollRow) => boolean;
  // Libelle de portee temporelle (ex. « 1 janv. – 31 mars 2026 ») quand on
  // regarde une sous-periode. Absent ⇒ on affiche l'annee.
  periodLabel?: string;
  // ADR-088 regle 5 : la regle des heures supplementaires que le serveur a appliquee
  // (catalogue) ; null quand aucune heure speciale ne l a demandee.
  overtimeRule?: OvertimeRule | null;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const specialTooltip = overtimeRule
    ? t('gest:planification.realTable.specialTooltip', {
        threshold: formatDecimal(overtimeRule.weeklyThresholdHours, locale),
        multiplier: formatDecimal(overtimeRule.multiplier, locale),
      })
    : t('gest:planification.realTable.specialTooltipNeutral');
  const rowCost = realPayrollRowCost;

  // Lignes comptees dans le total vs lignes affichees mais exclues.
  const countedRows = rows.filter(countRow);
  const excludedRows = rows.filter((r) => !countRow(r));
  const specialTotal = countedRows.reduce((sum, r) => sum + r.specialHours, 0);
  const premiumsTotal = countedRows.reduce((sum, r) => sum + r.premiums, 0);
  const benefitsTotal = countedRows.reduce((sum, r) => sum + r.benefits, 0);
  const bonusTotal = countedRows.reduce((sum, r) => sum + r.bonus, 0);
  const total = countedRows.reduce((sum, r) => sum + rowCost(r), 0);
  const excludedTotal = excludedRows.reduce((sum, r) => sum + rowCost(r), 0);
  // Non-tarifes estimes a la grille (comptes) vs vraiment sans taux (exclus).
  const estimatedCount = countedRows.filter((r) => r.unpriced && r.gridAnnual !== null).length;
  const unpricedCount = countedRows.filter((r) => r.unpriced && r.gridAnnual === null).length;
  const vehicleUnpricedCount = rows.filter((r) => r.vehicleUnpriced).length;
  const daysTotal = rows.some((r) => !r.unpriced && r.coverageDays < 365);
  // Libelles de portee : l'annee par defaut, sinon l'intervalle de sous-periode.
  const scope = periodLabel ?? String(year);
  const realHeader = periodLabel
    ? t('gest:planification.realTable.realHeaderPeriod', { period: periodLabel })
    : t('gest:planification.realTable.realHeaderYear', { year });
  // Colonnes additionnelles affichees seulement si des extras existent.
  const showExtras = rows.some(
    (r) => r.specialHours !== 0 || r.premiums !== 0 || r.benefits !== 0 || r.bonus !== 0,
  );
  // Colonnes de gauche (jusqu'a « Taux reel »/Ecart).
  const leftCols = showTeam ? 6 : 5;
  const baseColSpan = showExtras ? leftCols + 1 : leftCols;

  return (
    <div className="flex flex-col gap-2">
      {vehicleUnpricedCount > 0 ? (
        <div
          role="status"
          className="rounded-xl border-l-4 border-amber-500 bg-amber-50 p-4 text-field text-amber-700 dark:bg-amber-950 dark:text-amber-300"
        >
          <p className="flex items-center gap-1.5 font-semibold">
            <AlertTriangle size={16} aria-hidden="true" />
            {t('gest:planification.realTable.vehicleAlertTitle')}
          </p>
          <p className="mt-1 text-meta">
            {t('gest:planification.realTable.vehicleAlertBody', {
              count: vehicleUnpricedCount,
              year,
            })}
          </p>
        </div>
      ) : null}
      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full border-collapse text-field">
          <thead>
            <tr className="bg-slate-100/70 text-meta uppercase tracking-wide text-slate-500 dark:bg-slate-800/60 dark:text-slate-400">
              <th className="px-3 py-2 text-left font-medium">
                {t('gest:planification.realTable.employeeHeader')}
              </th>
              {showTeam ? (
                <th className="px-3 py-2 text-left font-medium">
                  {t('gest:planification.realTable.teamHeader')}
                </th>
              ) : null}
              <th className="px-3 py-2 text-left font-medium">
                {t('gest:planification.realTable.classHeader')}
              </th>
              <th className="px-3 py-2 text-right font-medium">
                {t('gest:planification.realTable.gridRateHeader', { year })}
              </th>
              <th className="px-3 py-2 text-right font-medium">
                {t('gest:planification.realTable.realRateHeader')}
              </th>
              <th className="px-3 py-2 text-right font-medium">
                {t('gest:planification.realTable.gapHeader')}
              </th>
              <th className="px-3 py-2 text-right font-medium">{realHeader}</th>
              {showExtras ? (
                <>
                  <th
                    className="px-3 py-2 text-right font-medium"
                    title={specialTooltip}
                  >
                    {t('gest:planification.realTable.specialHeader')}
                  </th>
                  <th
                    className="px-3 py-2 text-right font-medium"
                    title={t('gest:planification.realTable.premiumsTooltip')}
                  >
                    {t('gest:planification.realTable.premiumsHeader')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('gest:planification.realTable.benefitsHeader')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('gest:planification.realTable.bonusHeader')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('gest:planification.realTable.totalHeader', { scope })}
                  </th>
                </>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.email}
                className={`border-t border-slate-200 dark:border-slate-700${r.active ? '' : ' opacity-60'}`}
              >
                <td className="px-3 py-2">
                  <span className="inline-flex items-center gap-2">
                    {r.name}
                    {!r.active ? (
                      <Badge tone="neutral">{t('gest:planification.realTable.inactiveBadge')}</Badge>
                    ) : null}
                    {r.vehicleUnpriced ? (
                      <Badge tone="warning">{t('gest:planification.realTable.vehicleBadge')}</Badge>
                    ) : null}
                  </span>
                </td>
                {showTeam ? (
                  <td className="px-3 py-2 text-slate-500 dark:text-slate-400">{r.teamName ?? '—'}</td>
                ) : null}
                <td className="px-3 py-2 text-slate-500 dark:text-slate-400">
                  {r.offGrid ? (
                    <span className="inline-flex items-center gap-1.5">
                      {r.classLabel ?? '—'}
                      <Badge tone="warning">
                        {r.offGridDirection === 'above'
                          ? t('gest:planification.realTable.hgAbove')
                          : r.offGridDirection === 'below'
                            ? t('gest:planification.realTable.hgBelow')
                            : t('gest:planification.realTable.hg')}
                      </Badge>
                    </span>
                  ) : r.classLabel ? (
                    t('gest:planification.realTable.classEchelon', {
                      class: r.classLabel,
                      echelon: r.echelon,
                    })
                  ) : (
                    '—'
                  )}
                </td>
                <td
                  className="px-3 py-2 text-right tabular-nums"
                  title={
                    r.offGrid && r.offGridReferenceEchelon !== null
                      ? t('gest:planification.realTable.gridRateOffGridTitle', {
                          echelon: r.offGridReferenceEchelon,
                        })
                      : undefined
                  }
                >
                  {r.gridRate !== null ? (
                    <>
                      {formatHourly(r.gridRate, locale)}
                      {r.offGrid && r.offGridReferenceEchelon !== null ? (
                        <span className="text-slate-500 dark:text-slate-400">
                          {' '}
                          {t('gest:planification.realTable.refEchelon', {
                            echelon: r.offGridReferenceEchelon,
                          })}
                        </span>
                      ) : null}
                    </>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="px-3 py-2 text-right font-medium tabular-nums">
                  {r.realRate !== null ? formatHourly(r.realRate, locale) : '—'}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {r.gapDelta !== null ? (
                    <span className="text-slate-500 dark:text-slate-400">
                      {formatSignedHourly(r.gapDelta, locale)}
                      {r.gapPct !== null ? ` (${formatSignedPct(r.gapPct, locale)})` : ''}
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {r.unpriced ? (
                    r.gridAnnual !== null ? (
                      <span title={t('gest:planification.realTable.estimatedGridTitle')}>
                        {formatCad(r.gridAnnual, locale)}
                        <span className="text-slate-500 dark:text-slate-400">
                          {' '}
                          {t('gest:planification.realTable.gridSuffix')}
                        </span>
                      </span>
                    ) : (
                      <span className="text-slate-500 dark:text-slate-400">
                        {t('gest:planification.realTable.noRate')}
                      </span>
                    )
                  ) : (
                    <>
                      {formatCad(r.annual, locale)}
                      {r.coverageDays < 365 ? (
                        <span className="text-slate-500 dark:text-slate-400">
                          {' '}
                          {t('gest:planification.realTable.daysShort', { days: r.coverageDays })}
                        </span>
                      ) : null}
                    </>
                  )}
                </td>
                {showExtras ? (
                  <>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {r.specialHours !== 0 ? (
                        formatCad(r.specialHours, locale)
                      ) : (
                        <span className="text-slate-500 dark:text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {r.premiums !== 0 ? (
                        formatCad(r.premiums, locale)
                      ) : (
                        <span className="text-slate-500 dark:text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {r.benefits !== 0 ? (
                        formatCad(r.benefits, locale)
                      ) : (
                        <span className="text-slate-500 dark:text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {r.bonus !== 0 ? (
                        <>
                          {formatCad(r.bonus, locale)}
                          {r.bonusActualPct !== null ? (
                            <span className="text-slate-500 dark:text-slate-400">
                              {' · '}
                              {formatPctPlain(r.bonusActualPct, locale)}
                            </span>
                          ) : null}
                        </>
                      ) : (
                        <span className="text-slate-500 dark:text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-medium tabular-nums">
                      {formatCad(rowCost(r), locale)}
                    </td>
                  </>
                ) : null}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-slate-200 bg-slate-100/50 dark:border-slate-700 dark:bg-slate-800/40">
              <td className="px-3 py-2 font-medium" colSpan={baseColSpan}>
                {t('gest:planification.realTable.footTotal', { scope })}
                {estimatedCount > 0
                  ? ` ${t('gest:planification.realTable.footEstimated', { count: estimatedCount })}`
                  : ''}
                {unpricedCount > 0
                  ? ` ${t('gest:planification.realTable.footUnpriced', { count: unpricedCount })}`
                  : ''}
              </td>
              {showExtras ? (
                <>
                  <td className="px-3 py-2 text-right font-medium tabular-nums">
                    {specialTotal !== 0 ? formatCad(specialTotal, locale) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right font-medium tabular-nums">
                    {premiumsTotal !== 0 ? formatCad(premiumsTotal, locale) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right font-medium tabular-nums">
                    {benefitsTotal !== 0 ? formatCad(benefitsTotal, locale) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right font-medium tabular-nums">
                    {bonusTotal !== 0 ? formatCad(bonusTotal, locale) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold tabular-nums">
                    {formatCad(total, locale)}
                  </td>
                </>
              ) : (
                <td className="px-3 py-2 text-right font-semibold tabular-nums">
                  {formatCad(total, locale)}
                </td>
              )}
            </tr>
          </tfoot>
        </table>
      </div>
      {excludedRows.length > 0 ? (
        <p className="text-meta text-slate-500 dark:text-slate-400">
          {t('gest:planification.realTable.excludedNote', { count: excludedRows.length })}
          {excludedTotal > 0
            ? ` ${t('gest:planification.realTable.excludedAmount', { amount: formatCad(excludedTotal, locale) })}`
            : ''}
        </p>
      ) : null}
      {daysTotal ? (
        <p className="text-meta text-slate-500 dark:text-slate-400">
          {t('gest:planification.realTable.daysNote')}
        </p>
      ) : null}
    </div>
  );
}
